// New (v2) dashboard orchestrator. Fetches section availability once and renders each section
// only when the backend reports it as available for the current user. Sections own their own
// range/role state and data fetching.
import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import { CompanyModuleEnum, DashboardAvailabilityDTO } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import { DashboardPrefsProvider } from './dashboardPrefs.tsx';
import TaskTiles from './sections/TaskTiles.tsx';
import ShipmentsSection from './sections/ShipmentsSection.tsx';
import ReservationsSection from './sections/ReservationsSection.tsx';
import SupplierNotificationsSection from './sections/SupplierNotificationsSection.tsx';
import CustomsDeclarationsSection from './sections/CustomsDeclarationsSection.tsx';

const DashboardV2: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { getDashboardAvailability } = useContext(ApiContext);
  const [availability, setAvailability] = useState<DashboardAvailabilityDTO>();

  useEffect(() => {
    getDashboardAvailability(data => setAvailability(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shipments = availability?.statistics?.shipments;
  const reservations = availability?.statistics?.reservations;
  const supplierNotifications = availability?.statistics?.supplierNotifications;
  const customsDeclarations = availability?.statistics?.customsDeclarations;

  // Each section is gated by the real company module (hasModule) combined with the backend
  // reporting it as available for the current user.
  const shipmentsFlag = useSecuredFlag('dashboard-section-shipments', {
    description: 'Shipments statistics',
    modules: [{ module: CompanyModuleEnum.EXPEDITION }, { module: CompanyModuleEnum.CARRIER }],
    data: [{ label: 'shipments stats available', satisfied: !!(shipments && (shipments.asConsignee || shipments.asCarrier)) }],
  });
  const reservationsFlag = useSecuredFlag('dashboard-section-reservations', {
    description: 'Reservation statistics',
    modules: [{ module: CompanyModuleEnum.TIMESLOTS }],
    data: [{ label: 'reservation stats available', satisfied: !!(reservations && (reservations.myCompany || reservations.asPartner)) }],
  });
  const supplierNotificationsFlag = useSecuredFlag('dashboard-section-supplier-notifications', {
    description: 'Supplier notification statistics',
    modules: [{ module: CompanyModuleEnum.SUPPLIER_NOTIFICATION }],
    data: [{ label: 'supplier notification stats available', satisfied: !!(supplierNotifications && (supplierNotifications.myCompany || supplierNotifications.asPartner)) }],
  });
  // Customs declarants only ever INHERIT the module (from the customer that appointed them).
  const customsDeclarationsFlag = useSecuredFlag('dashboard-section-customs-declarations', {
    description: 'Customs declaration statistics',
    modules: [{ module: CompanyModuleEnum.CUSTOMS_DECLARATION, inherited: true }],
    data: [{ label: 'customs declaration stats available', satisfied: !!customsDeclarations?.available }],
  });

  return (
    <DashboardPrefsProvider>
      <div className="flex flex-col gap-6 pt-6 font-['DM_Sans']">
        <Heading title={tr('dashboard.title', 'Dashboard')} headingVariant={'h1'} />
        {availability?.tasks && <TaskTiles availability={availability.tasks} />}
        <SecuredArea id="dashboard-section-shipments" visible={shipmentsFlag} label="Shipments statistics" description="Dashboard statistics of shipments">
          {shipments && <ShipmentsSection availability={shipments} />}
        </SecuredArea>
        <SecuredArea id="dashboard-section-reservations" visible={reservationsFlag} label="Reservation statistics" description="Dashboard statistics of reservations">
          {reservations && <ReservationsSection availability={reservations} />}
        </SecuredArea>
        <SecuredArea id="dashboard-section-supplier-notifications" visible={supplierNotificationsFlag} label="Supplier notification statistics" description="Dashboard statistics of supplier notifications">
          {supplierNotifications && <SupplierNotificationsSection availability={supplierNotifications} />}
        </SecuredArea>
        <SecuredArea id="dashboard-section-customs-declarations" visible={customsDeclarationsFlag} label="Customs declaration statistics" description="Dashboard statistics of customs declarations">
          {customsDeclarations?.available && <CustomsDeclarationsSection />}
        </SecuredArea>
      </div>
    </DashboardPrefsProvider>
  );
};

export default DashboardV2;